import { useState, useEffect, useCallback, useRef } from "react";

export default function ReactionPlayer({ phase, signal, roundEnd, early, myName, scores, round, maxRounds, onPress }) {
  const [btnState, setBtnState] = useState("idle"); // idle | pressed | early | result_win | result_lose | trap | missed
  const pressedRef = useRef(false); // защита от двойного нажатия

  // Сброс при новом раунде — по round (чтобы сбрасывать даже если phase не менялась, напр. после фальстарта)
  useEffect(() => {
    setBtnState("idle");
    pressedRef.current = false;
  }, [round]);

  // Сигнал пришёл — показываем цвет
  useEffect(() => {
    if (phase === "signal") {
      if (!pressedRef.current) {
        setBtnState(signal?.real ? "ready" : "trap");
      }
    }
  }, [phase, signal]);

  // Результат раунда
  useEffect(() => {
    if (phase !== "result" || !roundEnd) return;
    if (roundEnd.is_fake) {
      const pressed = roundEnd.round_scores && myName in roundEnd.round_scores;
      setBtnState(pressed ? "trap_hit" : "trap_safe");
    } else if (roundEnd.winner === myName) {
      setBtnState("winner");
    } else if (pressedRef.current) {
      setBtnState("too_slow");
    } else {
      setBtnState("missed");
    }
  }, [phase, roundEnd, myName]);

  // Фальстарт
  useEffect(() => {
    if (early?.name === myName) setBtnState("early_hit");
  }, [early, myName]);

  const handlePress = useCallback(() => {
    if (pressedRef.current) return;
    pressedRef.current = true;
    setBtnState("pressed");
    onPress();
  }, [onPress]);

  const myScore = scores?.[myName] ?? 0;

  // Цвета и тексты для каждого состояния
  const STATE = {
    idle:       { bg: "#1a0845", border: "#ffffff22", emoji: "👁️", text: "Жди...", color: "#aaa", glow: "none" },
    ready:      { bg: "#0d3d1a", border: "#3dffa0",   emoji: "⚡", text: "ЖМИ!", color: "#3dffa0", glow: "0 0 40px #3dffa055" },
    trap:       { bg: "#3d0d0d", border: "#ff3355",   emoji: "💀", text: "ЛОВУШКА!", color: "#ff3355", glow: "0 0 40px #ff335555" },
    pressed:    { bg: "#0d3d1a", border: "#3dffa0",   emoji: "✅", text: "Нажато!", color: "#3dffa0", glow: "none" },
    winner:     { bg: "#0d2b1a", border: "#3dffa0",   emoji: "🏆", text: "Ты первый!", color: "#3dffa0", glow: "0 0 40px #3dffa055" },
    too_slow:   { bg: "#1a1a2b", border: "#ffffff22", emoji: "😅", text: "Опоздал", color: "#aaa", glow: "none" },
    missed:     { bg: "#1a1a2b", border: "#ffffff22", emoji: "😴", text: "Не успел", color: "#555", glow: "none" },
    trap_hit:   { bg: "#2b0d0d", border: "#ff3355",   emoji: "💀", text: "Попался!", color: "#ff3355", glow: "none" },
    trap_safe:  { bg: "#0d2b1a", border: "#3dffa0",   emoji: "😎", text: "Умно!", color: "#3dffa0", glow: "none" },
    early_hit:  { bg: "#2b1a0d", border: "#ff8c00",   emoji: "⚠️", text: "Рано!", color: "#ff8c00", glow: "none" },
  };

  const s = STATE[btnState] || STATE.idle;
  const canPress = phase === "waiting" || phase === "signal";

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, userSelect: "none", WebkitUserSelect: "none" }}>

      {/* Раунд + очки */}
      <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
        <div style={{ fontFamily: "'Nunito',sans-serif", color: "#aaa", fontSize: "0.85rem" }}>
          Раунд {round}/{maxRounds}
        </div>
        <div style={{ fontFamily: "'Fredoka One',sans-serif", color: "#ffe600", fontSize: "1.1rem" }}>
          {myScore} очков
        </div>
      </div>

      {/* Главная кнопка */}
      <div
        onTouchStart={(e) => { e.preventDefault(); if (canPress && !pressedRef.current) handlePress(); }}
        onClick={() => { if (canPress && !pressedRef.current) handlePress(); }}
        style={{
          width: "75vw", maxWidth: 300, height: "75vw", maxHeight: 300,
          borderRadius: "50%",
          background: s.bg,
          border: `6px solid ${s.border}`,
          boxShadow: s.glow,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          cursor: canPress ? "pointer" : "default",
          transition: "background 0.08s, border-color 0.08s, box-shadow 0.15s",
          WebkitTapHighlightColor: "transparent",
          touchAction: "manipulation",
        }}
      >
        <div style={{ fontSize: "3.5rem", lineHeight: 1 }}>{s.emoji}</div>
        <div style={{
          fontFamily: "'Fredoka One',sans-serif", fontSize: "1.5rem",
          color: s.color, textShadow: "2px 2px 0 #000", marginTop: 8,
          textAlign: "center",
        }}>{s.text}</div>
      </div>

      {/* Подсказка */}
      <div style={{ fontFamily: "'Nunito',sans-serif", color: "#444", fontSize: "0.8rem", textAlign: "center", maxWidth: 280 }}>
        {btnState === "idle" && "Нажми как только увидишь зелёный сигнал"}
        {btnState === "ready" && "Жми! Жми! Жми!"}
        {btnState === "trap" && "Не жми — это ловушка!"}
        {btnState === "winner" && `Реакция: ${roundEnd?.winner_ms}мс 🔥`}
        {btnState === "too_slow" && roundEnd?.winner && `Первый: ${roundEnd.winner} (${roundEnd.winner_ms}мс)`}
        {btnState === "trap_hit" && `-${Math.abs(roundEnd?.round_scores?.[myName] || 300)} очков 😬`}
        {btnState === "trap_safe" && "Не попался на ловушку +0 👍"}
        {btnState === "early_hit" && "Нажал до сигнала — штраф!"}
        {btnState === "missed" && "В следующий раз!"}
      </div>

    </div>
  );
}